'use client'

import searchImg from 'assets/blogs/search.svg'
import {useDebounce} from 'hooks/useDebounce'
import Image from 'next/image'
import {usePathname, useSearchParams} from 'next/navigation'
import * as React from 'react'
import classes from './styles.module.css'

interface DebouncedInputProps {
  onSearch: (text: string) => void
  setSearchedBlogs?: any
  setSearchText?: any
  debounceMs?: number
  immediate?: boolean
  serverSide?: boolean
}

export default function DebouncedInput({
  onSearch,
  debounceMs = 2000,
  immediate = false,
  serverSide = false,
}: DebouncedInputProps) {
  const searchParams = useSearchParams()
  const pathname = usePathname()

  // Determine the correct initial value based on current page
  const getInitialSearchTerm = React.useCallback(() => {
    // Only show search term if we're on the search results page
    if (pathname === '/blogs' && searchParams.get('search')) {
      return searchParams.get('search') || ''
    }
    // For all other pages (/blogs/category/:id, /blogs without search), show empty
    return ''
  }, [pathname, searchParams])

  const [searchTerm, setSearchTerm] = React.useState(getInitialSearchTerm())
  const [hasUserTyped, setHasUserTyped] = React.useState(false) // More specific flag
  const [isInitialized, setIsInitialized] = React.useState(false)
  const debouncedSearchTerm = useDebounce(searchTerm, debounceMs)
  const previousSearchTerm = React.useRef(searchTerm)

  // Initialize only once when component mounts
  React.useEffect(() => {
    if (!isInitialized) {
      const initialValue = getInitialSearchTerm()
      setSearchTerm(initialValue)
      previousSearchTerm.current = initialValue
      setIsInitialized(true)
    }
  }, [getInitialSearchTerm, isInitialized])

  // Reset search term when navigating away from search results
  React.useEffect(() => {
    if (isInitialized && !hasUserTyped) {
      const shouldShowSearchTerm = pathname === '/blogs' && searchParams.get('search')
      if (!shouldShowSearchTerm && searchTerm) {
        // Clear the search term when not on search results page
        setSearchTerm('')
        previousSearchTerm.current = ''
      } else if (shouldShowSearchTerm) {
        // Update search term when on search results page
        const currentQuery = searchParams.get('search') || ''
        if (currentQuery !== searchTerm) {
          setSearchTerm(currentQuery)
          previousSearchTerm.current = currentQuery
        }
      }
    }
  }, [pathname, searchParams, isInitialized, hasUserTyped, searchTerm])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    setHasUserTyped(true) // User has actually typed something

    if (immediate && !serverSide && value.trim()) {
      onSearch(value)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setHasUserTyped(true)
    onSearch(searchTerm)
  }

  // Only trigger search when user has actually typed AND the search term has changed
  React.useEffect(() => {
    if (
      !immediate &&
      !serverSide &&
      hasUserTyped &&
      debouncedSearchTerm !== previousSearchTerm.current
    ) {
      onSearch(debouncedSearchTerm)
      previousSearchTerm.current = debouncedSearchTerm
    }
  }, [debouncedSearchTerm, immediate, onSearch, serverSide, hasUserTyped])

  return (
    <form className={classes.search} onSubmit={handleSubmit}>
      <Image src={searchImg} alt="" />
      <input
        type="text"
        placeholder="What are you looking for?"
        value={searchTerm}
        onChange={handleChange}
        // Remove onFocus and onBlur handlers that were causing the issue
      />
      <button type="submit" style={{display: 'none'}}></button>
    </form>
  )
}
